import React, { useEffect, useState } from 'react';
import { Card, CardContent, Typography, Button, CircularProgress, Stack, TextField, InputAdornment, Box } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SearchIcon from '@mui/icons-material/Search';
import { useContent } from '../../../../context/ContentContext';
import ReactPlayer from 'react-player';
import download from 'downloadjs';  // Import downloadjs

// Debounce function to limit the rate of API calls
const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);

    return debouncedValue;
};

const SupportKitDisplay = () => {
    const { SupportKitData, loading, error, fetchSupportKitData } = useContent();
    const [searchQuery, setSearchQuery] = useState('');
    const debouncedSearchQuery = useDebounce(searchQuery, 500); // Set the debounce delay to 500ms

    useEffect(() => {
        if (debouncedSearchQuery || searchQuery === '') {
            fetchSupportKitData(debouncedSearchQuery);
        }
    }, [debouncedSearchQuery, searchQuery, fetchSupportKitData]);

    if (loading) {
        return (
            <Stack
                sx={{ width: '100%', height: '100vh', justifyContent: 'center', alignItems: 'center' }}
                direction="column"
            >
                <CircularProgress />
            </Stack>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', minWidth: '100%' }}>
            <Box
                sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 10,
                    backgroundColor: 'transparent',
                    padding: '8px',
                    marginBottom: 2,
                    width: '100%',
                }}
            >
                <TextField
                    label="Search Support Kits"
                    variant="outlined"
                    fullWidth
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    slotProps={{
                        input: {
                            endAdornment: (
                                <InputAdornment position="end">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        },
                    }}
                />
            </Box>

            <Box sx={{ flex: 1, overflowY: 'auto', width: '100%' }}>
                {/* Display message when no SupportKitData */}
                {(!SupportKitData || SupportKitData.length === 0) && (
                    <Stack
                        sx={{ width: '100%', justifyContent: 'center', alignItems: 'center' }}
                        direction="column"
                        spacing={2}
                    >
                        <Typography variant="body">
                            Hasil pencarian untuk keyword <span style={{ fontWeight: 'bold' }}>{searchQuery}</span>.
                        </Typography>
                        <img
                            src="/images/NoResultFound.webp"
                            alt="No Support Kit Found"
                            style={{ width: '300px', height: 'auto' }}
                        />
                        <Typography variant="body">
                            Mohon periksa kembali pencarian Anda.
                        </Typography>
                    </Stack>
                )}

                <Stack spacing={3} sx={{ p: 2 }}>
                    {SupportKitData?.map((kit) => (
                        <Card
                            key={kit.id}
                            sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', sm: 'column' },
                                alignItems: 'center',
                                padding: 2,
                            }}
                        >
                            <CardContent sx={{ flex: 1, textAlign: { xs: 'left', sm: 'left' } }}>
                                <Typography variant="h6" gutterBottom>
                                    <span dangerouslySetInnerHTML={{ __html: kit.title }} />
                                </Typography>
                            </CardContent>

                            {/* Check if it's a video or image and render accordingly */}
                            {kit.file && kit.file.endsWith('.mp4') ? (
                                <Stack
                                    sx={{
                                        width: '100%',
                                        paddingTop: 2,
                                        position: 'relative',
                                        paddingBottom: '56.25%', // Maintain 16:9 ratio
                                    }}
                                >
                                    <ReactPlayer
                                        url={kit.file}
                                        controls
                                        width="100%"
                                        height="100%"
                                        style={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            bottom: 0,
                                            objectFit: 'cover',
                                        }}
                                    />
                                </Stack>
                            ) : (
                                <img
                                    src={kit.file}
                                    alt={kit.title}
                                    style={{
                                        width: '100%',
                                        maxWidth: '300px',
                                        height: '100%',
                                        objectFit: 'cover',
                                    }}
                                />
                            )}
                        </Card>
                    ))}
                </Stack>
            </Box>
        </Box>
    );
};

export default SupportKitDisplay;
